import { type FC, useCallback } from "react";

import dompurify from "isomorphic-dompurify";

import { Button } from "@jti/core-components/components/Button";
import { dispatchTrackingEvent, TrackingEvents } from "@jti/tracker";

import type { QuizBannerContent } from "../types";
import { replacePointsPlaceholder } from "../types";

import styles from "./QuizBannerBottom.module.scss";

const { sanitize } = dompurify;

export type QuizBannerBottomProps = {
  points: number | null;
  isCompleted: boolean | null;
  content: QuizBannerContent;
  onStart: () => void;
};

const QUIZ_BANNER_CTA_LOCATION = "quiz-banner";

export const QuizBannerBottom: FC<QuizBannerBottomProps> = ({ points, isCompleted, content, onStart }) => {
  const pointsValue = points ?? 0;
  const { bottomBannerRedeemCta: redeemText, bottomBannerRedeemUrl: redeemUrl, bottomBannerCta: ctaText } = content;

  const handleRedeemClick = useCallback(() => {
    dispatchTrackingEvent(TrackingEvents.TrackingCtaClick, {
      cta_name: redeemText,
      cta_location: QUIZ_BANNER_CTA_LOCATION,
    });
  }, [redeemText]);

  if (isCompleted) {
    const earnedTitle = replacePointsPlaceholder(content.bottomBannerEarnedTitle, pointsValue);

    return (
      <div className={styles.wrapper} data-testid="quizBannerBottomEarned">
        <p className={styles.title} dangerouslySetInnerHTML={{ __html: sanitize(earnedTitle) }} />
        <div className={styles.action}>
          <Button
            variant="primary"
            size="sm"
            fullWidth
            href={redeemUrl}
            onClick={handleRedeemClick}
            className={styles.mobileButton}
            data-testid="quizBannerRedeemMobile"
          >
            {redeemText}
          </Button>
          <Button
            variant="primary"
            href={redeemUrl}
            onClick={handleRedeemClick}
            className={styles.desktopButton}
            data-testid="quizBannerRedeemDesktop"
          >
            {redeemText}
          </Button>
        </div>
      </div>
    );
  }

  const title = replacePointsPlaceholder(content.bottomBannerTitle, pointsValue);

  return (
    <div className={styles.wrapper}>
      <p className={styles.title} dangerouslySetInnerHTML={{ __html: sanitize(title) }} />
      <div className={styles.action}>
        <Button
          variant="primary"
          size="sm"
          fullWidth
          onClick={onStart}
          className={styles.mobileButton}
          data-testid="quizBannerBottomStartMobile"
        >
          {ctaText}
        </Button>
        <Button
          variant="primary"
          onClick={onStart}
          className={styles.desktopButton}
          data-testid="quizBannerBottomStartDesktop"
        >
          {ctaText}
        </Button>
      </div>
    </div>
  );
};
